<template>
  <div class="assignees" :class="`assignees--${size}`" @click.stop>
    <div v-for="user in visibleAssignees" :key="user.id" class="assignees__avatar-wrap">
      <v-tooltip location="top" :text="fullName(user)">
        <template #activator="{ props: tipProps }">
          <span
            v-bind="tipProps"
            class="assignees__avatar"
            :class="`assignees__avatar--${size}`"
            :style="avatarStyle(user)"
            >{{ initialsOf(user) }}</span
          >
        </template>
      </v-tooltip>
      <button
        v-if="!readonly"
        type="button"
        class="assignees__remove"
        :aria-label="`Unassign ${fullName(user)}`"
        @click.stop="onToggle(user.id, false)">
        <v-icon size="9">mdi-close</v-icon>
      </button>
    </div>
    <v-tooltip v-if="overflowCount > 0" location="top" :text="overflowNames">
      <template #activator="{ props: tipProps }">
        <span
          v-bind="tipProps"
          class="assignees__avatar assignees__avatar--overflow"
          :class="`assignees__avatar--${size}`"
          >+{{ overflowCount }}</span
        >
      </template>
    </v-tooltip>
    <v-menu v-if="!readonly" v-model="menuOpen" :close-on-content-click="false" location="bottom start">
      <template #activator="{ props: menuProps }">
        <button
          v-bind="menuProps"
          type="button"
          class="assignees__add"
          :class="`assignees__add--${size}`"
          aria-label="Assign teammate"
          @click.stop>
          <v-icon :size="size === 'md' ? 14 : 15">mdi-account-plus-outline</v-icon>
          <span v-if="size === 'md'">Assign</span>
        </button>
      </template>
      <v-card min-width="250" class="assignees__menu">
        <div class="assignees__search">
          <v-icon size="14" class="assignees__search-icon">mdi-magnify</v-icon>
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="assignees__search-input"
            placeholder="Filter teammates..."
            aria-label="Filter teammates"
            @keydown="onSearchKeydown" />
        </div>
        <div ref="listEl" class="assignees__list">
          <div
            v-for="(candidate, index) in filteredCandidates"
            :key="candidate.id"
            :data-index="index"
            class="assignees__item"
            :class="{ 'assignees__item--active': index === highlightedIndex }"
            @mousedown.prevent
            @mousemove="highlightedIndex = index"
            @click.stop="onToggle(candidate.id, !assignedIds.has(candidate.id))">
            <span class="assignees__avatar assignees__avatar--sm" :style="avatarStyle(candidate)">{{
              initialsOf(candidate)
            }}</span>
            <span class="assignees__item-label">{{ candidate.isMe ? "Me" : fullName(candidate) }}</span>
            <v-icon v-if="assignedIds.has(candidate.id)" size="16" color="accent">mdi-check</v-icon>
          </div>
          <div v-if="!filteredCandidates.length" class="assignees__empty">
            {{ candidates.length ? "No teammates match" : "No teammates yet" }}
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { AVATAR_COLORS, TAG_COLORS } from "@/constants/tagColors";
import { assignmentsService } from "@/services/assignmentsService";
import { useAuthStore, useActionNeededStore, useInboxStore } from "@/stores";

const props = defineProps({
  assignees: { type: Array, default: () => [] },
  entityType: { type: String, required: true, validator: (v) => ["document", "order", "submission"].includes(v) },
  entityId: { type: [String, Number], required: true },
  size: { type: String, default: "sm", validator: (v) => ["sm", "md"].includes(v) },
});

const emit = defineEmits(["update:assignees"]);

const authStore = useAuthStore();
const actionNeededStore = useActionNeededStore();
const inboxStore = useInboxStore();

// Assignments are admin-managed for assigned-cases-only users (LAM-4850) — the
// backend 403s their mutations, so render the bubbles display-only.
const readonly = computed(() => actionNeededStore.assignedCasesOnly ?? authStore.userInfo?.assignedCasesOnly === true);

// Grid cells cap at 3 bubbles so the "+N" chip always fits inline in the
// fixed-width column; free-width surfaces (detail bars, mobile cards) show 4.
const maxVisible = computed(() => (props.size === "sm" ? 3 : 4));
const assignedIds = computed(() => new Set((props.assignees || []).map((a) => a.id)));
const visibleAssignees = computed(() => (props.assignees || []).slice(0, maxVisible.value));
const overflowCount = computed(() => Math.max(0, (props.assignees || []).length - maxVisible.value));
const overflowNames = computed(() =>
  (props.assignees || [])
    .slice(maxVisible.value)
    .map((u) => fullName(u))
    .join(", ")
);

const me = computed(() => {
  const info = authStore.userInfo || {};
  if (info.pk == null) return null;
  return { id: info.pk, firstName: info.firstName, lastName: info.lastName, email: info.email, isMe: true };
});

const candidates = computed(() => {
  const teammates = (inboxStore.teammates || []).map((t) => ({ ...t, isMe: false }));
  return me.value ? [me.value, ...teammates] : teammates;
});

const menuOpen = ref(false);
const search = ref("");
const highlightedIndex = ref(-1);
const searchInput = ref(null);
const listEl = ref(null);

const filteredCandidates = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return candidates.value;
  return candidates.value.filter(
    (candidate) =>
      fullName(candidate).toLowerCase().includes(query) ||
      (candidate.email || "").split("@")[0].toLowerCase().includes(query)
  );
});

watch(menuOpen, (open) => {
  if (open) {
    inboxStore.fetchTeammates().catch(() => {});
    resetHighlight();
    nextTick(() =>
      requestAnimationFrame(() => {
        pinListHeight();
        searchInput.value?.focus();
        requestAnimationFrame(() => searchInput.value?.focus());
      })
    );
  } else {
    search.value = "";
    highlightedIndex.value = -1;
    listEl.value?.style.removeProperty("min-height");
  }
});

watch([search, candidates], () => {
  resetHighlight();
  if (menuOpen.value && !search.value) nextTick(pinListHeight);
});

function pinListHeight() {
  if (listEl.value) listEl.value.style.minHeight = `${listEl.value.offsetHeight}px`;
}

function resetHighlight() {
  highlightedIndex.value = filteredCandidates.value.length ? 0 : -1;
}

function moveHighlight(step) {
  const count = filteredCandidates.value.length;
  if (!count) return;
  const start = highlightedIndex.value < 0 && step < 0 ? 0 : highlightedIndex.value;
  const index = (start + step + count) % count;
  highlightedIndex.value = index;
  nextTick(() => listEl.value?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" }));
}

function toggleHighlighted() {
  const candidate = filteredCandidates.value[highlightedIndex.value];
  if (candidate) onToggle(candidate.id, !assignedIds.value.has(candidate.id));
}

function onSearchKeydown(event) {
  if (event.isComposing) return;
  const actions = {
    ArrowDown: () => moveHighlight(1),
    ArrowUp: () => moveHighlight(-1),
    Enter: toggleHighlighted,
    Escape: () => (menuOpen.value = false),
  };
  const action = actions[event.key];
  if (!action) return;
  event.preventDefault();
  event.stopPropagation();
  action();
}

function fullName(user) {
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "Unknown user";
}

function initialsOf(user) {
  const initials = `${(user.firstName || "").charAt(0)}${(user.lastName || "").charAt(0)}`.toUpperCase();
  return initials || (user.email || "?").charAt(0).toUpperCase();
}

function avatarStyle(user) {
  const color = AVATAR_COLORS[user.id % AVATAR_COLORS.length] || TAG_COLORS.grey;
  return { background: `color-mix(in srgb, ${color} 16%, white)`, color };
}

async function onToggle(userId, assign) {
  const previous = props.assignees || [];
  let next;
  if (assign) {
    const user = candidates.value.find((c) => c.id === userId);
    if (!user || assignedIds.value.has(userId)) return;
    next = [...previous, { id: user.id, firstName: user.firstName, lastName: user.lastName, email: user.email }];
  } else {
    next = previous.filter((a) => a.id !== userId);
  }
  emit("update:assignees", next);
  try {
    const { assignees } = await assignmentsService.setAssignees(
      props.entityType,
      props.entityId,
      next.map((a) => a.id)
    );
    emit("update:assignees", assignees);
  } catch {
    emit("update:assignees", previous);
  }
}
</script>

<style lang="scss" scoped>
// Grid cells (sm) stay on ONE line by construction: 22px bubbles overlapped
// -8px mean even "3 + overflow + add" tops out around 100px inside the 140px
// column. Only the free-width md variant (detail bars, mobile cards) wraps.
.assignees {
  display: inline-flex;
  align-items: center;

  &--md {
    flex-wrap: wrap;
    row-gap: 6px;
  }
}

.assignees__add {
  white-space: nowrap;
  flex-shrink: 0;
}

.assignees__avatar-wrap {
  position: relative;

  &:not(:first-child) {
    margin-left: -5px;
  }

  &:hover {
    z-index: 2;

    .assignees__remove {
      opacity: 1;
    }
  }
}

.assignees__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: $lh_font_family_dm_sans;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px #fff;

  &--sm {
    width: 26px;
    height: 26px;
    font-size: 10.5px;
  }

  &--md {
    width: 30px;
    height: 30px;
    font-size: 12px;
  }

  &--overflow {
    background: $lh_color_snowy_white;
    color: $lh_color_blue_gray;
    margin-left: -5px;
  }
}

.assignees__remove {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 13px;
  height: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: $lh_color_navy;
  color: #fff;
  cursor: pointer;
  opacity: 0;
  transition: opacity $lh_transition_fast;
  z-index: 3;
}

.assignees__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed rgba($lh_color_navy, 0.25);
  background: transparent;
  color: $lh_color_blue_gray;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color $lh_transition_fast, border-color $lh_transition_fast, background $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    border-color: rgba($lh_color_blue, 0.45);
    background: rgba($lh_color_blue, 0.04);
  }

  &--sm {
    width: 26px;
    height: 26px;
    padding: 0;
    flex: 0 0 auto;
    border-radius: 50%;
    margin-left: 7px;
  }

  &--md {
    height: 30px;
    padding: 0 12px 0 9px;
    border-radius: 15px;
    margin-left: 8px;
  }
}

.assignees__menu {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  border-radius: 12px !important;
}

.assignees__search {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 10px 12px 8px;
  border-bottom: 1px solid rgba($lh_color_navy, 0.08);
}

.assignees__search-icon {
  color: $lh_color_blue_gray;
  flex: 0 0 auto;
}

.assignees__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: $lh_font_family_dm_sans;
  font-size: 13.5px;
  color: $lh_color_navy;

  &::placeholder {
    color: $lh_color_blue_gray;
  }
}

.assignees__list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 8px;
}

.assignees__item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: $lh_font_family_dm_sans;
  transition: background $lh_transition_fast;

  &--active {
    background: $lh_color_snowy_white;
  }
}

.assignees__item-label {
  font-size: 13.5px;
  font-weight: 500;
  color: $lh_color_navy;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assignees__empty {
  margin: auto;
  padding: 10px;
  font-size: 13px;
  color: $lh_color_blue_gray;
}
</style>
